import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';

const LINKAGE_FORMULA_CAPTION =
  '(wheel mm = a + b·s + c·s² + d·s³, s = shock stroke mm)';

/** Single-line a–d row + “Curve definition” title and formula caption. */
function LinkageCurveDefinitionRow({ pf, main, updateMain, embedCustomSource }) {
  const applyCoeff = (key, raw) => {
    const n = Number(raw);
    const patch = { [key]: Number.isFinite(n) ? n : 0 };
    if (embedCustomSource) patch.linkageSource = 'custom';
    updateMain(patch);
  };

  const coeffs = [
    { key: 'rearA', letter: 'a', step: '0.0001' },
    { key: 'rearB', letter: 'b', step: '0.0001' },
    { key: 'rearC', letter: 'c', step: '0.000001' },
    { key: 'rearD', letter: 'd', step: '0.00000001' },
  ];

  return (
    <>
      <div className="jack-linkage-curve-heading">Curve definition</div>
      <p className="jack-linkage-curve-formula">{LINKAGE_FORMULA_CAPTION}</p>
      <div className="jack-linkage-curve-row" aria-label="Linkage polynomial coefficients">
        {coeffs.map(({ key, letter, step }) => (
          <label key={key} className="jack-linkage-coeff" htmlFor={`${pf}linkage-${key}`}>
            <span className="jack-linkage-coeff-label">{letter}</span>
            <input
              id={`${pf}linkage-${key}`}
              className="form-input"
              type="number"
              step={step}
              value={main[key]}
              onChange={(e) => applyCoeff(key, e.target.value)}
            />
          </label>
        ))}
      </div>
    </>
  );
}

function LinkageCsvFileRow({ id, onChange, label }) {
  return (
    <label className="menu-item" style={{ marginBottom: 10, display: 'inline-flex', alignItems: 'center' }}>
      <input id={id} type="file" accept=".csv,.txt" onChange={onChange} />
      <span style={{ marginLeft: 8 }}>{label}</span>
    </label>
  );
}

/**
 * Travel (mm) + linkage preset picker + custom leverage curve (a–d), shared by Settings and onboarding.
 * All behavior is driven by props from `useJackLinkageHandlers` + parent `main` / `updateMain`.
 */
function JackTravelLinkageFields({
  main,
  updateMain,
  linkageSelectValue,
  onLinkageSelect,
  sortedLinkagePresets,
  communityPresets,
  communityListError,
  handleLinkageCsv,
  linkagePresetNameInput,
  setLinkagePresetNameInput,
  handleSaveNewLinkagePreset,
  /** When set, shows Edit next to delete for saved presets and persists changes via this callback. */
  handleUpdateSavedLinkagePreset,
  linkageSavedSelected,
  deleteCurrentLinkagePreset,
  linkageStatus,
  idPrefix = '',
  /** Optional: admin “publish public” checkbox etc., shown under “Save as new linkage preset”. */
  linkagePresetFooterSlot = null,
}) {
  const pf = idPrefix ? `${idPrefix}-` : '';
  const [savedPresetEditorOpen, setSavedPresetEditorOpen] = useState(false);

  useEffect(() => {
    if (!linkageSavedSelected) setSavedPresetEditorOpen(false);
  }, [linkageSavedSelected]);

  const canEditSavedPreset =
    Boolean(linkageSavedSelected) && typeof handleUpdateSavedLinkagePreset === 'function';

  return (
    <>
      <p style={{ color: '#888', fontSize: 13, margin: '0 0 12px' }}>Travel</p>
      <div className="jack-stats-grid jack-stats-grid--2" style={{ marginBottom: 22 }}>
        <div>
          <label className="profile-email jack-settings-label" htmlFor={`${pf}frontTravel`}>
            Front travel (mm)
          </label>
          <input
            id={`${pf}frontTravel`}
            className="form-input"
            type="number"
            min="0"
            max="300"
            value={main.frontTravel}
            onChange={(e) => updateMain({ frontTravel: Number(e.target.value) })}
          />
        </div>
        <div>
          <label className="profile-email jack-settings-label" htmlFor={`${pf}rearTravel`}>
            Rear travel (mm)
          </label>
          <input
            id={`${pf}rearTravel`}
            className="form-input"
            type="number"
            min="0"
            max="300"
            value={main.rearTravel}
            onChange={(e) => updateMain({ rearTravel: Number(e.target.value) })}
          />
        </div>
      </div>

      <h4 style={{ color: '#ccc', margin: '0 0 6px', fontSize: 15, fontWeight: 600 }}>Linkage source</h4>
      <p style={{ color: '#666', fontSize: 12, margin: '0 0 8px' }}>
        Rear wheel vs shock curve (a–d). Saved and community presets replace the curve; they do not change travel
        unless the community document includes travel fields.
      </p>
      <div className="jack-settings-stack" style={{ marginBottom: 12 }}>
        <label className="profile-email jack-settings-label" htmlFor={`${pf}linkagePreset`}>
          Linkage preset
        </label>
        <div className="jack-settings-toolbar">
          <select
            id={`${pf}linkagePreset`}
            className="form-input"
            value={linkageSelectValue}
            onChange={onLinkageSelect}
          >
            {sortedLinkagePresets.length > 0 ? (
              <optgroup label="My saved (this account)">
                {sortedLinkagePresets.map((lp) => (
                  <option key={lp.id} value={`saved:${lp.id}`}>
                    {lp.name}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {communityPresets.length > 0 ? (
              <optgroup label="Public library (admin & community)">
                {communityPresets.map((p) => (
                  <option key={p.id} value={`community:${p.id}`}>
                    {p.publicLabel || p.id}
                  </option>
                ))}
              </optgroup>
            ) : null}
            <option value="custom">Custom linkage…</option>
          </select>
          {canEditSavedPreset ? (
            <button
              type="button"
              className="jack-icon-trash"
              onClick={() => setSavedPresetEditorOpen((o) => !o)}
              aria-label={savedPresetEditorOpen ? 'Close linkage preset editor' : 'Edit linkage preset'}
              title={savedPresetEditorOpen ? 'Close editor' : 'Edit coefficients and name'}
            >
              <FaEdit aria-hidden />
            </button>
          ) : null}
          {linkageSavedSelected ? (
            <button
              type="button"
              className="jack-icon-trash"
              onClick={deleteCurrentLinkagePreset}
              aria-label="Delete linkage preset"
              title="Delete linkage preset"
            >
              <FaTrash aria-hidden />
            </button>
          ) : null}
        </div>
      </div>
      {canEditSavedPreset && savedPresetEditorOpen ? (
        <div className="jack-linkage-subpanel" style={{ marginTop: 12 }}>
          <h4 style={{ color: '#e0e0e0', margin: '0 0 8px', fontSize: 15 }}>Edit saved linkage preset</h4>
          <p style={{ color: '#888', fontSize: 12, margin: '0 0 12px' }}>
            Upload a CSV or edit the curve, then save. Linear leverage is usually in <code style={{ color: '#bbb' }}>b</code>
            ; keep <code style={{ color: '#bbb' }}>d</code> at 0 unless you use a cubic fit.
          </p>
          <label className="profile-email jack-settings-label" htmlFor={`${pf}linkagePresetNameEdit`}>
            Preset name
          </label>
          <input
            id={`${pf}linkagePresetNameEdit`}
            className="form-input"
            style={{ maxWidth: 480, marginBottom: 12 }}
            value={linkagePresetNameInput}
            onChange={(e) => setLinkagePresetNameInput(e.target.value)}
          />
          <LinkageCsvFileRow
            id={`${pf}linkage-csv-edit`}
            label="Upload linkage CSV"
            onChange={(e) => handleLinkageCsv(e, { keepLinkageSource: true })}
          />
          <LinkageCurveDefinitionRow pf={pf} main={main} updateMain={updateMain} embedCustomSource={false} />
          <div className="jack-settings-inline-row" style={{ marginTop: 14, flexWrap: 'wrap', gap: 10 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                if (handleUpdateSavedLinkagePreset()) setSavedPresetEditorOpen(false);
              }}
            >
              Save changes to preset
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setSavedPresetEditorOpen(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
      {communityListError ? (
        <p style={{ color: '#c98a6e', fontSize: 13, marginTop: 10, marginBottom: 0 }}>{communityListError}</p>
      ) : null}

      {main.linkageSource === 'custom' ? (
        <div className="jack-linkage-subpanel">
          <h4 style={{ color: '#e0e0e0', margin: '0 0 8px', fontSize: 15 }}>Custom linkage curve</h4>
          <p style={{ color: '#888', fontSize: 12, margin: '0 0 10px' }}>
            Upload a CSV (shock position, wheel travel) or set coefficients below, then save as a named preset.
          </p>
          <LinkageCsvFileRow
            id={`${pf}linkage-csv-custom`}
            label="Upload linkage CSV"
            onChange={handleLinkageCsv}
          />
          <LinkageCurveDefinitionRow pf={pf} main={main} updateMain={updateMain} embedCustomSource />

          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #2a2a2a' }}>
            <label className="profile-email jack-settings-label" htmlFor={`${pf}linkagePresetName`}>
              Linkage preset name
            </label>
            <div className="jack-settings-inline-row">
              <input
                id={`${pf}linkagePresetName`}
                className="form-input"
                placeholder="Name for this curve"
                value={linkagePresetNameInput}
                onChange={(e) => setLinkagePresetNameInput(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  const r = handleSaveNewLinkagePreset();
                  if (r && typeof r.then === 'function') r.catch(() => {});
                }}
              >
                Save as new linkage preset
              </button>
            </div>
            {linkagePresetFooterSlot}
          </div>
        </div>
      ) : null}

      {linkageStatus ? <div style={{ color: '#888', fontSize: 13, marginTop: 12 }}>{linkageStatus}</div> : null}
    </>
  );
}

export default JackTravelLinkageFields;
